import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Lottie from 'lottie-react'
import { Button } from '@/components/ui'
import confettiLottie from '@/assets/lottie/confetti.json'
import { useAnalysisStore } from '@/store/analysisStore'
import { useSurveyStore } from '@/store/surveyStore'
import { useReportStore } from '@/store/reportStore'
import { useToastStore } from '@/store/toastStore'
import { signOut as firebaseSignOut } from '@/lib/firebase'
import { useBackPrevent } from '@/hooks/useBackPrevent'
import { trackFunnelEvent, trackEngagementEvent } from '@/services/analytics'
import { getSeverityLevel, getSeverityColor } from '@/types/analysis'
import type { SeverityLevel } from '@/types/analysis'
import type { Product } from '@/types/product'
import { haptic } from '@/lib/haptics'
import { debugLog } from '@/lib/logger'
import { logError } from '@/services/errorLogger'
import { getProductTags, CONCERN_COLORS, type UserConcerns } from '@/utils/productTags'

// Get user age (default to 30 if not provided)
function getUserAge(age: number | null): number {
  return age ?? 30
}

// Severity label and color helper
function getSeverityLabel(level: SeverityLevel): string {
  switch (level) {
    case 'mild': return 'Mild'
    case 'moderate': return 'Moderate'
    case 'severe': return 'Severe'
  }
}

// Average comparison - scores are skin health (higher = better)
type AverageComparison = 'above' | 'average' | 'below'

function getAverageComparison(score: number): AverageComparison {
  if (score >= 70) return 'above'  // High score = above average skin
  if (score >= 50) return 'average'
  return 'below'  // Low score = below average skin
}

function getAverageColor(comparison: AverageComparison): string {
  switch (comparison) {
    case 'above': return '#22c55e'  // green
    case 'average': return '#f59e0b'  // amber
    case 'below': return '#ef4444'  // red
  }
}

export function TeaserPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { analysisResults } = useAnalysisStore()
  const { shortSurvey } = useSurveyStore()
  const { recommendedRoutine, isLoadingRoutine, loadRealRoutines } = useReportStore()
  const showToast = useToastStore((state) => state.showToast)

  // Show welcome toast if redirected from another page (once per session)
  useEffect(() => {
    const state = location.state as { showWelcomeToast?: boolean } | null
    const toastKey = 'k-skin-teaser-welcome-shown'
    if (state?.showWelcomeToast && !sessionStorage.getItem(toastKey)) {
      sessionStorage.setItem(toastKey, 'true')
      showToast('Welcome back! Here is your free preview.', 'info', 5000)
      window.history.replaceState({}, document.title)
    }
  }, [location.state, showToast])

  // Route guard: redirect to survey if no analysis results
  useEffect(() => {
    if (!analysisResults) {
      navigate('/survey', { replace: true })
    }
  }, [analysisResults, navigate])

  // Track teaser viewed
  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true
      trackFunnelEvent.teaserViewed(!!analysisResults)
    }
  }, [analysisResults])

  // Prevent back button navigation
  useBackPrevent("Your free preview is ready here! Sign up to see your full report.")
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null)
  const [showPackageModal, setShowPackageModal] = useState(false)
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right')
  const [showMoreMetrics, setShowMoreMetrics] = useState(false)
  const [hasClickedMetric, setHasClickedMetric] = useState(false)
  const [carouselSlide, setCarouselSlide] = useState(0)
  const [morningCenteredCard, setMorningCenteredCard] = useState(2)
  const [nightCenteredCard, setNightCenteredCard] = useState(4)
  const lottieRef = useRef<any>(null)
  const skinAgeBoxRef = useRef<HTMLDivElement>(null)
  const morningCarouselRef = useRef<HTMLDivElement>(null)
  const nightCarouselRef = useRef<HTMLDivElement>(null)

  // Play confetti when skin age box comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && lottieRef.current) {
            lottieRef.current.goToAndPlay(0)
          }
        })
      },
      { threshold: 0.5 }
    )

    if (skinAgeBoxRef.current) {
      observer.observe(skinAgeBoxRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Redirect to photo upload if no analysis results
  useEffect(() => {
    if (!analysisResults) {
      navigate('/photo', { replace: true })
    }
  }, [analysisResults, navigate])

  // Load product recommendations
  useEffect(() => {
    if (analysisResults && !recommendedRoutine && !isLoadingRoutine) {
      debugLog('[TeaserPage] Loading recommendations...')
      loadRealRoutines('targeted')
    }
  }, [analysisResults, recommendedRoutine, isLoadingRoutine, loadRealRoutines])

  // Set initial scroll position immediately (no animation)
  useEffect(() => {
    const cardWidth = 234
    const gap = 12

    const setInitialScroll = (carousel: HTMLDivElement, cardIndex: number) => {
      const containerWidth = carousel.clientWidth
      const cardCenter = cardIndex * (cardWidth + gap) + cardWidth / 2
      carousel.scrollLeft = cardCenter - containerWidth / 2
    }

    // Small delay to ensure elements are rendered
    const timer = setTimeout(() => {
      if (morningCarouselRef.current) {
        setInitialScroll(morningCarouselRef.current, 2) // Step 3
      }
      if (nightCarouselRef.current) {
        setInitialScroll(nightCarouselRef.current, 4) // Step 4 (5th box)
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [])

  // Auto-scroll carousels to center unlocked cards when they come back into view
  useEffect(() => {
    const cardWidth = 234
    const gap = 12
    let morningFirstView = true
    let nightFirstView = true

    const scrollToCard = (carousel: HTMLDivElement, cardIndex: number) => {
      const containerWidth = carousel.clientWidth
      const cardCenter = cardIndex * (cardWidth + gap) + cardWidth / 2
      const targetScroll = cardCenter - containerWidth / 2
      const startScroll = carousel.scrollLeft
      const distance = targetScroll - startScroll
      const duration = 800
      let startTime: number | null = null

      const animateScroll = (currentTime: number) => {
        if (!startTime) startTime = currentTime
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easeProgress = 1 - Math.pow(1 - progress, 3) // ease-out cubic
        carousel.scrollLeft = startScroll + distance * easeProgress
        if (progress < 1) {
          requestAnimationFrame(animateScroll)
        }
      }
      requestAnimationFrame(animateScroll)
    }

    const morningObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            morningFirstView = false
          } else if (!morningFirstView && morningCarouselRef.current) {
            scrollToCard(morningCarouselRef.current, 2) // Step 3 (index 2)
          }
        })
      },
      { threshold: 0.5 }
    )

    const nightObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            nightFirstView = false
          } else if (!nightFirstView && nightCarouselRef.current) {
            scrollToCard(nightCarouselRef.current, 4) // Step 4 (5th box)
          }
        })
      },
      { threshold: 0.5 }
    )

    if (morningCarouselRef.current) {
      morningObserver.observe(morningCarouselRef.current)
    }
    if (nightCarouselRef.current) {
      nightObserver.observe(nightCarouselRef.current)
    }

    return () => {
      morningObserver.disconnect()
      nightObserver.disconnect()
    }
  }, [])

  // Detect centered card on scroll for scale effect
  useEffect(() => {
    const cardWidth = 234
    const gap = 12

    const getCenteredCard = (carousel: HTMLDivElement) => {
      const scrollLeft = carousel.scrollLeft
      const containerWidth = carousel.clientWidth
      const centerPoint = scrollLeft + containerWidth / 2
      const cardIndex = Math.round((centerPoint - cardWidth / 2) / (cardWidth + gap))
      return Math.max(0, cardIndex)
    }

    const handleMorningScroll = () => {
      if (morningCarouselRef.current) {
        setMorningCenteredCard(getCenteredCard(morningCarouselRef.current))
      }
    }

    const handleNightScroll = () => {
      if (nightCarouselRef.current) {
        setNightCenteredCard(getCenteredCard(nightCarouselRef.current))
      }
    }

    morningCarouselRef.current?.addEventListener('scroll', handleMorningScroll)
    nightCarouselRef.current?.addEventListener('scroll', handleNightScroll)

    return () => {
      morningCarouselRef.current?.removeEventListener('scroll', handleMorningScroll)
      nightCarouselRef.current?.removeEventListener('scroll', handleNightScroll)
    }
  }, [])

  // Auto-scroll carousel in bottom sheet
  useEffect(() => {
    if (!showPackageModal) {
      setCarouselSlide(0)
      return
    }
    const interval = setInterval(() => {
      setCarouselSlide((prev) => (prev + 1) % 4)
    }, 3000)
    return () => clearInterval(interval)
  }, [showPackageModal])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showPackageModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showPackageModal])

  // Carousel slides data
  const carouselSlides = [
    {
      title: 'Full',
      highlight: '14-metric',
      titleEnd: 'HD skin analysis',
      description: 'Go beyond basics with clinic-level insights into aging, hydration, tone, and sensitivity.',
    },
    {
      title: '10+',
      highlight: 'personalized',
      titleEnd: 'K-beauty products',
      description: 'AI-matched formulas based on your unique skin data.',
    },
    {
      title: 'Custom',
      highlight: 'AM & PM skincare',
      titleEnd: 'routines',
      description: 'Step-by-step routines designed for your skin, lifestyle, and goals.',
    },
    {
      title: '',
      highlight: 'Ingredient',
      titleEnd: 'recommendations',
      description: 'Know which ingredients help and which to avoid, based on your analysis.',
    },
  ]

  // SAVED FOR LATER - Slide 4 (PDF report):
  // {
  //   title: 'Downloadable',
  //   highlight: 'PDF',
  //   titleEnd: 'report',
  //   description: 'Keep a clear, shareable record of your skin data and recommendations.',
  //   image: 'carousel-slide-4.png'
  // }

  // Don't render until we have results
  if (!analysisResults) {
    return null
  }

  const results = analysisResults

  // Debug: Log metrics from store
  debugLog('[TeaserPage] Metrics from store:', {
    pores: results.metrics.pores,
    texture: results.metrics.texture,
    wrinkles: results.metrics.wrinkles,
    redness: results.metrics.redness,
    skinScore: results.skinScore,
    skinAge: results.skinAge,
  })

  const userAge = getUserAge(shortSurvey.age)
  const skinAgeDiff = results.skinAge - userAge
  const isOlder = skinAgeDiff > 0

  // Get recommended products for unlocked cards
  const getUnlockedProduct = (time: 'am' | 'pm', category: string): { product: Product; matchReason: string } | null => {
    if (!recommendedRoutine) return null
    const steps = time === 'am' ? recommendedRoutine.am : recommendedRoutine.pm
    const step = steps.find(s => s.category === category)
    if (!step) return null
    const product = step.products[step.selectedTier]
    return product ? {
      product,
      matchReason: step.matchReasons[0] || 'Suitable for your skin type',
    } : null
  }

  // AM Serum and PM Sheet Mask products
  const amSerum = getUnlockedProduct('am', 'serum')
  const pmSheetMask = getUnlockedProduct('pm', 'sheet-mask')

  // User's concerns from analysis for personalized hashtags
  const userConcerns: UserConcerns = {
    pores: results.metrics.pores ?? 0,
    texture: results.metrics.texture ?? 0,
    wrinkles: results.metrics.wrinkles ?? 0,
    redness: results.metrics.redness ?? 0,
  }

  // 4 SD metrics to display (unlocked) - wrinkles replaces spots (HD-only)
  const sdMetrics = [
    { id: 'pores', name: 'Pores', score: results.metrics.pores, heatmap: results.heatmaps.pores },
    { id: 'texture', name: 'Texture', score: results.metrics.texture, heatmap: results.heatmaps.texture },
    { id: 'wrinkles', name: 'Wrinkles', score: results.metrics.wrinkles, heatmap: results.heatmaps.wrinkles },
    { id: 'redness', name: 'Redness', score: results.metrics.redness, heatmap: results.heatmaps.redness },
  ]

  // Locked metrics for "see more" section
  const lockedSkinHealthMetrics = [
    { id: 'acne', name: 'Acne' },
    { id: 'oiliness', name: 'Oiliness' },
  ]

  const lockedAgingMetrics = [
    { id: 'spots', name: 'Age Spots' },  // HD-only, moved from sdMetrics
    { id: 'firmness', name: 'Firmness' },
    { id: 'radiance', name: 'Radiance' },
    { id: 'moisture', name: 'Moisture' },
  ]

  const lockedEyeMetrics = [
    { id: 'darkCircles', name: 'Dark Circles' },
    { id: 'eyeBags', name: 'Eye Bags' },
    { id: 'upperEyelid', name: 'Upper Eyelid' },
    { id: 'lowerEyelid', name: 'Lower Eyelid' },
  ]

  // Navigation for fullscreen modal
  const currentIndex = expandedMetric ? sdMetrics.findIndex(m => m.id === expandedMetric) : -1
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < sdMetrics.length - 1

  const goToPrev = useCallback(() => {
    if (hasPrev) {
      haptic('selection')
      setSlideDirection('left')
      setExpandedMetric(sdMetrics[currentIndex - 1].id)
    }
  }, [hasPrev, currentIndex, sdMetrics])

  const goToNext = useCallback(() => {
    if (hasNext) {
      haptic('selection')
      setSlideDirection('right')
      setExpandedMetric(sdMetrics[currentIndex + 1].id)
    }
  }, [hasNext, currentIndex, sdMetrics])

  // Keyboard navigation for fullscreen modal
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (!expandedMetric) return
      if (e.key === 'Escape') setExpandedMetric(null)
      if (e.key === 'ArrowLeft') goToPrev()
      if (e.key === 'ArrowRight') goToNext()
    }
    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [expandedMetric, goToPrev, goToNext])

  const handleUnlock = () => {
    trackFunnelEvent.teaserCtaClicked()
    navigate('/login')
  }

  return (
    <div className="min-h-dvh bg-[#FFFEF1] select-none overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FFFEF1]/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div
            className="text-[18px]"
            style={{ fontFamily: "'Oleo Script', cursive" }}
          >
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </div>
        </div>
      </header>

      {/* Main content - bottom padding for floating CTA */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-28">
        {/* HERO: Skin Age Shock */}
        <div className="relative">
          {/* Green star decoration behind the card */}
          <motion.img
            src="/image/Star_green.png"
            alt=""
            className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[240px] h-[240px] z-0 pointer-events-none"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Section Title */}
          <h2 className="text-[23px] font-bold text-[#FF8C33] mb-2 text-center relative z-10" style={{ fontFamily: "'Anybody', sans-serif" }}>Your Skin Age</h2>
          <motion.img
            src="/image/sprinkles1.png"
            alt=""
            className="absolute top-[-10px] right-0 w-[48px] h-auto z-20 pointer-events-none"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            ref={skinAgeBoxRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 rounded-2xl p-6 shadow-[0_0_8px_rgba(0,0,0,0.08)] mb-6 text-center relative overflow-hidden z-10"
          >
          {/* Confetti animation */}
          <div className="absolute inset-0 pointer-events-none flex items-start justify-center z-20" style={{ top: '-60px' }}>
            <Lottie
              lottieRef={lottieRef}
              animationData={confettiLottie}
              loop={false}
              autoplay={true}
              style={{ width: '100%', height: 'auto' }}
            />
          </div>

          <p className="text-gray-500 text-sm mb-0 relative z-10">Your AI Skin Analysis</p>

          <div className="mb-2 relative z-10">
            <span
                className="text-8xl font-bold text-[#46C0C6]"
                style={{ fontFamily: "'Passion One', cursive" }}
              >{results.skinAge}</span>
            <p className="text-gray-500 mt-0">Skin Age</p>
          </div>

          <div className={`inline-flex items-center gap-2 px-12 py-2 rounded-full text-base relative z-10 ${
            isOlder ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            {isOlder ? (
              <>
                <svg className="w-4 h-4 -ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
                <span className="font-medium">
                  {Math.abs(skinAgeDiff)} years OLDER than<br />your actual age ({userAge})
                </span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 -ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="font-medium">
                  {Math.abs(skinAgeDiff)} years YOUNGER than<br />your actual age ({userAge})
                </span>
              </>
            )}
          </div>

          {isOlder && (
            <p className="text-red-600 text-sm mt-3 relative z-10">
              Your skin is aging faster than it should be
            </p>
          )}
          {!isOlder && skinAgeDiff === 0 && (
            <p className="text-gray-600 text-sm mt-3 relative z-10">
              Your skin age matches your actual age
            </p>
          )}
          {!isOlder && skinAgeDiff < 0 && (
            <p className="text-green-600 text-sm mt-3 relative z-10">
              Great job! Let's keep your skin young
            </p>
          )}
        </motion.div>
        </div>

        {/* 4 SD Metrics - ALL VISIBLE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-[40px] mb-6"
        >
          <h2 className="text-[23px] font-bold text-[#FF8C33] mb-0 text-center" style={{ fontFamily: "'Anybody', sans-serif" }}>Skin Analysis in detail</h2>
          <p className="text-sm text-gray-500 mb-4 text-center">Click on each metric to view details</p>

          <p className="text-sm font-semibold text-[#46C0C6] mb-3 text-center">Skin Health</p>

          <div className="grid grid-cols-2 gap-3">
            {sdMetrics.map((metric) => {
              const avgComparison = getAverageComparison(metric.score)

              const buttonContent = (
                <button
                  key={metric.id}
                  onClick={() => {
                    haptic('selection')
                    setHasClickedMetric(true)
                    if (expandedMetric !== metric.id) {
                      trackEngagementEvent.metricExpanded(metric.id)
                    }
                    setExpandedMetric(expandedMetric === metric.id ? null : metric.id)
                  }}
                  className="bg-white rounded-xl p-3 shadow-[0_0_8px_rgba(0,0,0,0.08)] text-left transition-all relative z-10 w-full"
                >
                  <div className="flex gap-3 h-[70px]">
                    {/* Small thumbnail with heatmap */}
                    <div className="relative w-[50px] h-[70px] rounded-lg overflow-hidden flex-shrink-0">
                      {/* Base photo */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage: `url(${results.photoUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      {/* Heatmap overlay */}
                      {metric.heatmap && (
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundImage: `url(${metric.heatmap})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            mixBlendMode: 'multiply',
                            opacity: 0.6,
                          }}
                        />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <span className="font-semibold text-gray-900 text-sm">{metric.name}</span>
                      <div
                        className="text-xs font-medium"
                        style={{ color: getAverageColor(avgComparison) }}
                      >
                        {avgComparison === 'above' ? 'Above' : avgComparison === 'below' ? 'Below' : ''}<br />
                        Average
                      </div>
                    </div>
                  </div>

                  {/* Circular score gauge - bottom right */}
                  <div className="absolute bottom-2 right-2 w-[36px] h-[36px]">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                      {/* Background circle */}
                      <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="white"
                        stroke="#e5e7eb"
                        strokeWidth="3"
                        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                      />
                      {/* Progress arc */}
                      <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="none"
                        stroke={getAverageColor(avgComparison)}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={`${(metric.score / 100) * 94.2} 94.2`}
                      />
                    </svg>
                    <span
                      className="absolute inset-0 flex items-center justify-center text-xs font-bold"
                      style={{ color: getAverageColor(avgComparison) }}
                    >
                      {metric.score}
                    </span>
                  </div>
                </button>
              )

              // Wrap Pores box with click indicator
              if (metric.id === 'pores') {
                return (
                  <div key={metric.id} className="relative">
                    {!hasClickedMetric && (
                      <motion.img
                        src="/image/click.png"
                        alt="Click to view details"
                        className="absolute top-1/2 left-[calc(50%+28px)] -translate-x-1/2 -translate-y-[28%] w-[60px] h-auto z-30 pointer-events-none"
                        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.7, 0.5] }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                      />
                    )}
                    {buttonContent}
                  </div>
                )
              }

              // Wrap Texture box with star decoration
              if (metric.id === 'texture') {
                return (
                  <div key={metric.id} className="relative">
                    <motion.img
                      src="/image/Star_orange.png"
                      alt=""
                      className="absolute top-[-50px] right-[-70px] w-[269px] h-auto z-0 pointer-events-none"
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {buttonContent}
                  </div>
                )
              }

              return <div key={metric.id}>{buttonContent}</div>
            })}
          </div>

          {/* See more button */}
          {!showMoreMetrics && (
            <button
              onClick={() => setShowMoreMetrics(true)}
              className="w-full flex items-center justify-center gap-2 mt-4 py-3 text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-sm font-medium">see more</span>
            </button>
          )}

          {/* Locked metrics shown when "see more" is clicked */}
          {showMoreMetrics && (
            <>
              {/* Locked Skin Health metrics */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                {lockedSkinHealthMetrics.map((metric) => (
                  <div
                    key={metric.id}
                    className="bg-white rounded-xl p-3 shadow-[0_0_8px_rgba(0,0,0,0.08)] text-left relative"
                  >
                    <div className="flex gap-3 h-[70px]">
                      <div className="relative w-[50px] h-[70px] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 blur-[4px]">
                        <div
                          className="absolute inset-0 grayscale-[0.7]"
                          style={{
                            backgroundImage: `url(${results.photoUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <span className="font-semibold text-gray-900 text-sm">{metric.name}</span>
                        <div className="text-xs font-medium text-gray-400 blur-[4px]">
                          Average
                        </div>
                      </div>
                    </div>
                    {/* Lock icon overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    {/* Blurred gauge */}
                    <div className="absolute bottom-2 right-2 w-[36px] h-[36px] rounded-full bg-gray-200 blur-[4px]" />
                  </div>
                ))}
              </div>

              {/* Aging Signs section */}
              <p className="text-sm font-semibold text-[#46C0C6] mt-6 mb-3 text-center">Aging Signs</p>
              <div className="grid grid-cols-2 gap-3">
                {lockedAgingMetrics.map((metric) => {
                  const boxContent = (
                    <div
                      className="bg-white rounded-xl p-3 shadow-[0_0_8px_rgba(0,0,0,0.08)] text-left relative z-10"
                    >
                      <div className="flex gap-3 h-[70px]">
                        <div className="relative w-[50px] h-[70px] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 blur-[4px]">
                          <div
                            className="absolute inset-0 grayscale-[0.7]"
                            style={{
                              backgroundImage: `url(${results.photoUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <span className="font-semibold text-gray-900 text-sm">{metric.name}</span>
                          <div className="text-xs font-medium text-gray-400 blur-[4px]">
                            Average
                          </div>
                        </div>
                      </div>
                      {/* Lock icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      {/* Blurred gauge */}
                      <div className="absolute bottom-2 right-2 w-[36px] h-[36px] rounded-full bg-gray-200 blur-[4px]" />
                    </div>
                  )

                  if (metric.id === 'radiance') {
                    return (
                      <div key={metric.id} className="relative">
                        <motion.img
                          src="/image/Star_gray.png"
                          alt=""
                          className="absolute top-[-50px] right-[60px] w-[269px] h-auto z-0 pointer-events-none"
                          animate={{ y: [0, -8, 0] }}
                          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        />
                        {boxContent}
                      </div>
                    )
                  }

                  return <div key={metric.id}>{boxContent}</div>
                })}
              </div>

              {/* Eye Area section */}
              <p className="text-sm font-semibold text-[#46C0C6] mt-6 mb-3 text-center">Eye Area</p>
              <div className="grid grid-cols-2 gap-3">
                {lockedEyeMetrics.map((metric) => {
                  const boxContent = (
                    <div
                      className="bg-white rounded-xl p-3 shadow-[0_0_8px_rgba(0,0,0,0.08)] text-left relative z-10"
                    >
                      <div className="flex gap-3 h-[70px]">
                        <div className="relative w-[50px] h-[70px] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 blur-[4px]">
                          <div
                            className="absolute inset-0 grayscale-[0.7]"
                            style={{
                              backgroundImage: `url(${results.photoUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <span className="font-semibold text-gray-900 text-sm">{metric.name}</span>
                          <div className="text-xs font-medium text-gray-400 blur-[4px]">
                            Average
                          </div>
                        </div>
                      </div>
                      {/* Lock icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      {/* Blurred gauge */}
                      <div className="absolute bottom-2 right-2 w-[36px] h-[36px] rounded-full bg-gray-200 blur-[4px]" />
                    </div>
                  )

                  if (metric.id === 'lowerEyelid') {
                    return (
                      <div key={metric.id} className="relative">
                        <motion.img
                          src="/image/Star_green.png"
                          alt=""
                          className="absolute top-[-50px] right-[-70px] w-[269px] h-auto z-0 pointer-events-none"
                          animate={{ y: [0, -10, 0] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                        {boxContent}
                      </div>
                    )
                  }

                  return <div key={metric.id}>{boxContent}</div>
                })}
              </div>

              {/* 14 metrics footer */}
              <div className="flex items-center justify-center gap-2 mt-6 text-gray-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-sm">14 metrics in full report</span>
              </div>

              {/* See less button */}
              <button
                onClick={() => setShowMoreMetrics(false)}
                className="w-full flex items-center justify-center gap-2 mt-4 py-3 text-gray-600"
              >
                <svg className="w-4 h-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="text-sm font-medium">see less</span>
              </button>
            </>
          )}

              <h2 className="text-[23px] font-bold text-[#FF8C33] mt-10 text-center leading-tight" style={{ fontFamily: "'Anybody', sans-serif" }}>Your AI-matched<br />K-Beauty Guide</h2>
              <p className="text-sm text-gray-500 mt-1 leading-tight text-center">A complete K-beauty routine,<br />curated from 10,000+ products just for you.</p>

              {/* Morning Routine */}
              <div className="mt-6 relative">
                <motion.img src="/image/Star_gray.png" alt="" className="absolute top-[-40px] left-[-60px] w-[160px] h-auto z-0 pointer-events-none opacity-80" animate={{ y: [0, -8, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }} />
                <motion.img src="/image/Star_green.png" alt="" className="absolute bottom-[-60px] right-[-60px] w-[200px] h-auto z-0 pointer-events-none" animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
                <div className="absolute inset-0 bg-[#FF8C33]/20 backdrop-blur-md -mx-4 z-[1]" />
                <div className="relative z-10 py-4">
                  <div className="flex items-center justify-center gap-2 mb-3 relative">
                    <span className="text-lg">☀️</span>
                    <h3 className="text-xl font-semibold text-[#7182F8]">Morning Routine</h3>
                    <motion.img src="/image/sprinkles1.png" alt="" className="absolute right-0 top-1/2 -translate-y-1/2 w-[40px] h-auto pointer-events-none" animate={{ y: [0, -5, 0] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }} />
                  </div>

                  {/* Horizontal scroll container */}
                  <div ref={morningCarouselRef} className="flex gap-3 overflow-x-auto py-4 -mx-4 px-4 pr-[calc(50%-117px)] snap-x snap-mandatory [&::-webkit-scrollbar]:hidden overflow-y-visible" style={{ scrollbarWidth: 'none' }}>
                  {/* Step 1 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 0 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step1: Cleanser</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 2 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 1 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step2: Toner</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 3 - Unlocked (Dynamic from recommendation engine) */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 2 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center h-[207px] overflow-hidden">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step3: Serum</p>
                      {amSerum ? (
                        <>
                          <p className="text-base text-gray-700 mb-2 leading-tight"><span className="font-bold">{amSerum.product.brand}</span> {amSerum.product.name}</p>
                          <img src={amSerum.product.imageUrl || '/image/AM_Serum.jpg'} alt={amSerum.product.name} className="w-[100px] h-[130px] object-contain mx-auto -mt-[10px]" onError={(e) => { e.currentTarget.src = '/image/AM_Serum.jpg' }} />
                        </>
                      ) : (
                        <>
                          <p className="text-base text-gray-700 mb-2 leading-tight"><span className="font-bold">Anua</span> PDRN Hyaluronic Acid Capsule 100 Serum</p>
                          <img src="/image/AM_Serum.jpg" alt="Anua PDRN Serum" className="w-[100px] h-[130px] object-contain mx-auto -mt-[10px]" />
                        </>
                      )}
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      {amSerum ? (
                        <div className="flex-1 flex flex-col justify-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {getProductTags(amSerum.product, userConcerns, shortSurvey.skinType).map((tag, i) => {
                              const colors = tag.concernKey ? CONCERN_COLORS[tag.concernKey] : null
                              return (
                                <span
                                  key={i}
                                  className={`text-[10px] px-2 py-0.5 rounded-full ${
                                    tag.type === 'skinType'
                                      ? 'bg-[#46C0C6]/20 text-[#46C0C6]'
                                      : colors
                                        ? `${colors.bg} ${colors.text}`
                                        : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  #{tag.label}
                                </span>
                              )
                            })}
                          </div>
                          {amSerum.product.benefit && (
                            <p className="text-xs text-gray-500 text-center mt-1 line-clamp-3">{amSerum.product.benefit}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 leading-none flex-1 text-center">Suitable for sensitive skin and concerns for redness</p>
                      )}
                      <a
                        href={amSerum?.product.affiliateUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          if (!amSerum?.product.affiliateUrl) {
                            logError('MISSING_AFFILIATE_URL', `Product missing affiliate URL: ${amSerum?.product.name}`, `productId=${amSerum?.product.id}, brand=${amSerum?.product.brand}`)
                          }
                        }}
                        className="px-8 py-1.5 bg-white rounded-full text-base text-[#46C0C6] flex items-center justify-center gap-1 shadow-[0_0_8px_rgba(0,0,0,0.08)] mx-auto"
                      >
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>

                  {/* Step 4 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 3 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step4: Eye Cream</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 5 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 4 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step5: Moisturizer</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 6 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: morningCenteredCard === 5 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step6: Sunscreen</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              {/* Night Routine */}
              <div className="mt-6 relative">
                <div className="absolute inset-0 bg-[#9C98C9]/50 backdrop-blur-md -mx-4 z-[1]" />
                <div className="relative z-10 py-4">
                  <div className="flex items-center justify-center gap-2 mb-3 relative">
                    <motion.img src="/image/sprinkles2.png" alt="" className="absolute left-0 top-[calc(50%-10px)] -translate-y-1/2 w-[60px] h-auto pointer-events-none" animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
                    <span className="text-lg">🌙</span>
                    <h3 className="text-xl font-semibold text-[#7B3ED8]">Night Routine</h3>
                  </div>

                  {/* Horizontal scroll container */}
                  <div ref={nightCarouselRef} className="flex gap-3 overflow-x-auto py-4 -mx-4 px-4 pr-[calc(50%-117px)] snap-x snap-mandatory [&::-webkit-scrollbar]:hidden overflow-y-visible" style={{ scrollbarWidth: 'none' }}>
                  {/* Step 1 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 0 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step1: 1st Cleanser</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 2 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 1 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step2: 2nd Cleanser</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 2.5 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 2 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step3: Additional Care</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 3 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 3 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step4: Sheet Mask</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 4 - Unlocked (Dynamic from recommendation engine) */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 4 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center h-[207px] overflow-hidden">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step5: Serum</p>
                      {pmSheetMask ? (
                        <>
                          <p className="text-base text-gray-700 mb-2 leading-tight"><span className="font-bold">{pmSheetMask.product.brand}</span> {pmSheetMask.product.name}</p>
                          <img src={pmSheetMask.product.imageUrl || '/image/PM_SheetMask.jpg'} alt={pmSheetMask.product.name} className="w-[100px] h-[130px] object-contain mx-auto -mt-[10px]" onError={(e) => { e.currentTarget.src = '/image/PM_SheetMask.jpg' }} />
                        </>
                      ) : (
                        <>
                          <p className="text-base text-gray-700 mb-2 leading-tight"><span className="font-bold">Biodance</span> Hydro Cera-nol Real Deep Mask</p>
                          <img src="/image/PM_SheetMask.jpg" alt="Biodance Sheet Mask" className="w-[100px] h-[130px] object-contain mx-auto -mt-[10px]" />
                        </>
                      )}
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      {pmSheetMask ? (
                        <div className="flex-1 flex flex-col justify-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {getProductTags(pmSheetMask.product, userConcerns, shortSurvey.skinType).map((tag, i) => {
                              const colors = tag.concernKey ? CONCERN_COLORS[tag.concernKey] : null
                              return (
                                <span
                                  key={i}
                                  className={`text-[10px] px-2 py-0.5 rounded-full ${
                                    tag.type === 'skinType'
                                      ? 'bg-[#46C0C6]/20 text-[#46C0C6]'
                                      : colors
                                        ? `${colors.bg} ${colors.text}`
                                        : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  #{tag.label}
                                </span>
                              )
                            })}
                          </div>
                          {pmSheetMask.product.benefit && (
                            <p className="text-xs text-gray-500 text-center mt-1 line-clamp-3">{pmSheetMask.product.benefit}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 leading-none flex-1 text-center">Suitable for sensitive skin and concerns for redness</p>
                      )}
                      <a
                        href={pmSheetMask?.product.affiliateUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          if (!pmSheetMask?.product.affiliateUrl) {
                            logError('MISSING_AFFILIATE_URL', `Product missing affiliate URL: ${pmSheetMask?.product.name}`, `productId=${pmSheetMask?.product.id}, brand=${pmSheetMask?.product.brand}`)
                          }
                        }}
                        className="px-8 py-1.5 bg-white rounded-full text-base text-[#46C0C6] flex items-center justify-center gap-1 shadow-[0_0_8px_rgba(0,0,0,0.08)] mx-auto"
                      >
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>

                  {/* Step 5 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 5 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step6: Eye Cream</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 7 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 6 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step7: Moisturizer</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 7.5 - Locked */}
                  <div
                    className="flex-shrink-0 w-[234px] h-[332px] bg-white rounded-xl shadow-[0_0_8px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col snap-center transition-transform duration-200"
                    style={{ transform: nightCenteredCard === 7 ? 'scale(1.05)' : 'scale(1)' }}
                  >
                    <div className="p-3 pb-0 text-center">
                      <p className="text-base font-semibold text-[#46C0C6] mb-1">Step8: Treatment</p>
                      <p className="text-base text-gray-400 blur-[4px] mb-2">Product Name</p>
                      <div className="w-[100px] h-[130px] bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-[#ECF9F9] p-3 pb-6 mt-auto rounded-b-xl h-[115px] flex flex-col justify-between">
                      <p className="text-sm text-gray-400 blur-[4px] leading-none text-center">Suitable for your skin type</p>
                      <button className="px-8 py-1.5 bg-gray-200 rounded-full text-base text-gray-500 flex items-center justify-center gap-1 mx-auto">
                        Shop Now
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-xs text-gray-400 text-center"
        >
          K-Beauty AI provides cosmetic guidance only. This is not a medical
          diagnosis. For skin concerns, consult a licensed dermatologist.
        </motion.p>

        {/* Test: Restart button - DEV ONLY */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 mb-20 text-center"
        >
          <button
            onClick={async () => {
              // Sign out from Firebase first to clear auth session
              try {
                await firebaseSignOut()
              } catch (e) {
                console.error('Failed to sign out:', e)
              }
              // Clear all app data (localStorage stores + additional flags)
              localStorage.removeItem('k-skin-survey')
              localStorage.removeItem('k-skin-analysis')
              localStorage.removeItem('k-skin-auth')
              localStorage.removeItem('k-skin-full-survey')
              localStorage.removeItem('k-skin-report')
              localStorage.removeItem('k-skin-check-auth')
              localStorage.removeItem('k-skin-retake-used')
              localStorage.removeItem('k-skin-pending-redirect')
              sessionStorage.clear()
              // Force full page reload to clear in-memory Zustand state
              window.location.href = '/'
            }}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            Test: Restart User Flow
          </button>
        </motion.div>
      </main>

      {/* Floating CTA Button - positioned near bottom */}
      <div
        className="fixed left-4 right-4 z-40"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
        }}
      >
        <div className="max-w-md mx-auto">
          <Button
            fullWidth
            size="lg"
            onClick={() => setShowPackageModal(true)}
            className="shadow-xl"
          >
            Get Your Full Report
          </Button>
        </div>
      </div>

      {/* Package Details Modal */}
      <AnimatePresence>
        {showPackageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
            onClick={() => setShowPackageModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-gradient-to-b from-[#F5FCFC] to-white w-full max-w-lg rounded-t-3xl p-6 pb-8 safe-bottom relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative sparkles */}
              <motion.svg
                className="absolute top-8 left-6"
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="#FF8C33"
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7], rotate: [0, 15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
              </motion.svg>
              <motion.svg
                className="absolute top-16 right-8"
                width={10}
                height={10}
                viewBox="0 0 24 24"
                fill="#F596FA"
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7], rotate: [0, 15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
              </motion.svg>
              <motion.svg
                className="absolute bottom-32 left-4"
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="#7182F8"
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7], rotate: [0, 15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
              </motion.svg>

              {/* Close button */}
              <button
                onClick={() => {
                  haptic('light')
                  setShowPackageModal(false)
                }}
                className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Title with sparkle effect */}
              <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-bold text-[#FF8C33] text-center mt-2 mb-4"
                style={{ fontFamily: "'Anybody', sans-serif" }}
              >
                What's Included
              </motion.h2>

              {/* Image carousel */}
              <div className="flex justify-center mb-3 overflow-hidden relative">
                <motion.div
                  className="w-60 h-44 rounded-xl flex items-center justify-center relative cursor-grab active:cursor-grabbing z-10"
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(_, info) => {
                    if (info.offset.x < -50) {
                      setCarouselSlide((prev) => (prev + 1) % 4)
                    } else if (info.offset.x > 50) {
                      setCarouselSlide((prev) => (prev - 1 + 4) % 4)
                    }
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={carouselSlide}
                      src={`/image/carousel-slide-${[1, 2, 3, 5][carouselSlide]}.png`}
                      alt={`Slide ${carouselSlide + 1}`}
                      className="w-full h-full object-contain pointer-events-none"
                      initial={{ opacity: 0, x: 50, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -50, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* Pagination dots */}
              <div className="flex justify-center gap-2 mb-6">
                {[0, 1, 2, 3].map((index) => (
                  <button
                    key={index}
                    onClick={() => setCarouselSlide(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === carouselSlide ? 'bg-[#46C0C6] w-6' : 'bg-gray-300 w-2'
                    }`}
                  />
                ))}
              </div>

              {/* Feature text */}
              <div className="text-center mb-6 h-[100px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={carouselSlide}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      {carouselSlides[carouselSlide].title}{' '}
                      <span className="text-[#46C0C6]">{carouselSlides[carouselSlide].highlight}</span>{' '}
                      {carouselSlides[carouselSlide].titleEnd}
                    </p>
                    <p className="text-gray-500 text-sm leading-tight">
                      {carouselSlides[carouselSlide].description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Pricing box with entrance animation */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="relative bg-[#ECF9F9] border-2 border-gray-800 rounded-2xl p-1 mb-6"
              >
                {/* Animated discount badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 300 }}
                  className="absolute -top-3 right-4"
                >
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="bg-[#E85A4F] text-white text-base font-semibold px-4 py-0.5 rounded-full shadow-md"
                  >
                    70% discount
                  </motion.div>
                </motion.div>

                {/* Inner border */}
                <div className="border-2 border-[#B8E4E6] rounded-xl p-4">
                  <p className="text-gray-900 text-lg whitespace-nowrap leading-none">
                    <span className="font-bold">One-time</span> <span className="font-medium">payment of</span> <span className="font-bold line-through decoration-[#E85A4F] decoration-[2px]">$99</span>
                  </p>
                  <p className="text-gray-900 text-xl flex justify-between items-center leading-none -mt-1">
                    <span>Instant Access</span>
                    <span className="text-[#E85A4F] font-bold text-[30px]">→ $29</span>
                  </p>
                </div>
              </motion.div>

              {/* CTA Button with pulse animation */}
              <motion.div
                animate={{
                  boxShadow: [
                    "0 0 0 0 rgba(126, 217, 87, 0.4)",
                    "0 0 0 12px rgba(126, 217, 87, 0)",
                    "0 0 0 0 rgba(126, 217, 87, 0)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                className="rounded-full"
              >
                <Button
                  fullWidth
                  size="lg"
                  onClick={handleUnlock}
                  className="shadow-lg rounded-full"
                >
                  Sign-up for Full Report
                </Button>
              </motion.div>

              {/* Maybe later */}
              <button
                onClick={() => {
                  haptic('light')
                  setShowPackageModal(false)
                }}
                className="w-full text-center text-gray-500 text-sm mt-4 py-2 hover:text-gray-700 transition-colors"
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Metric Modal */}
      <AnimatePresence>
        {expandedMetric && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex flex-col"
            onClick={() => setExpandedMetric(null)}
          >
            {/* Close button */}
            <button
              onClick={() => {
                haptic('light')
                setExpandedMetric(null)
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Left (Previous) button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToPrev()
              }}
              disabled={!hasPrev}
              className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                hasPrev
                  ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            {/* Right (Next) button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
              }}
              disabled={!hasNext}
              className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                hasNext
                  ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            {/* Image container - takes up available space */}
            <div className="flex-1 flex items-center justify-center p-4">
              <AnimatePresence mode="wait" initial={false}>
                {(() => {
                  const metric = sdMetrics.find(m => m.id === expandedMetric)
                  if (!metric) return null
                  const severity = getSeverityLevel(metric.score)

                  return (
                    <motion.div
                      key={metric.id}
                      initial={{ x: slideDirection === 'right' ? 100 : -100, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: slideDirection === 'right' ? -100 : 100, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="relative w-full max-w-lg aspect-[3/4] rounded-2xl overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Base photo */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage: `url(${results.photoUrl})`,
                          backgroundSize: '100% 100%',
                        }}
                      />

                      {/* Heatmap overlay */}
                      {metric.heatmap && (
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundImage: `url(${metric.heatmap})`,
                            backgroundSize: '100% 100%',
                            mixBlendMode: 'multiply',
                            opacity: 0.85,
                          }}
                        />
                      )}

                      {/* Info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white text-xl font-semibold">{metric.name}</h3>
                          <span
                            className="text-sm font-medium px-3 py-1 rounded-full"
                            style={{
                              backgroundColor: `${getSeverityColor(severity)}`,
                              color: 'white',
                            }}
                          >
                            {getSeverityLabel(severity)}
                          </span>
                        </div>
                        <p className="text-white/80 text-sm">
                          Score: {metric.score}/100 • Areas with more intense color indicate higher concern.
                        </p>
                      </div>
                    </motion.div>
                  )
                })()}
              </AnimatePresence>
            </div>

            {/* Navigation indicator + hint */}
            <div className="shrink-0 pb-8 text-center safe-bottom space-y-2">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5">
                <span className="text-white text-sm font-medium">
                  {currentIndex + 1} / {sdMetrics.length}
                </span>
              </div>
              <p className="text-white/60 text-sm">Tap anywhere to close</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
